import React from 'react';
import { Clock, MessageSquare, ChevronRight, Store, User, Zap } from 'lucide-react';
import ListingImageWithStoreLogo from './ListingImageWithStoreLogo';

const ChatListItem = ({ 
  chat, 
  onClick, 
  formatTime,
  variant = 'desktop',
  isActive = false,
  currentUserId = null,
  hasNewMessage = false 
}) => {
  // Determine if current user is buyer or seller
  const isCurrentUserBuyer = currentUserId === chat.buyer_id;
  const isCurrentUserSeller = currentUserId === chat.seller_id;
  
  // Get display information based on role
  let displayName = '';
  let displayImage = null;
  let displayIcon = null;
  let isVerified = false;
  let isOnline = false;
  let storeName = '';
  
  if (isCurrentUserBuyer) {
    // User is buyer: Show seller/store info
    displayName = chat.seller_store_name || chat.seller_name || 
                  `${chat.seller_first_name || ''} ${chat.seller_last_name || ''}`.trim() || 
                  `Seller ${chat.seller_id}`;
    displayImage = chat.seller_store_logo || chat.seller_profile_picture;
    displayIcon = Store;
    isVerified = chat.seller_is_verified;
    isOnline = chat.seller_is_online;
    storeName = chat.seller_store_name;
  } else if (isCurrentUserSeller) {
    // User is seller: Show buyer info
    displayName = chat.buyer_name || 
                  `${chat.buyer_first_name || ''} ${chat.buyer_last_name || ''}`.trim() || 
                  `Buyer ${chat.buyer_id}`;
    displayImage = chat.buyer_profile_picture;
    displayIcon = User;
    isOnline = chat.buyer_is_online;
  } else {
    // Fallback (shouldn't happen)
    displayName = `User`;
  }

  const isMobile = variant === 'mobile';
  
  const containerClasses = isMobile 
    ? `p-4 transition-all duration-300 cursor-pointer ${
        hasNewMessage ? 'bg-blue-50 animate-pulse' : 'bg-white'
      } hover:bg-gray-50`
    : `p-5 transition-all duration-300 cursor-pointer ${
        isActive 
          ? 'bg-gradient-to-r from-orange-50 to-white border-l-4 border-[#ff660e]' 
          : `hover:bg-gray-50 ${hasNewMessage ? 'bg-blue-50' : 'bg-white'}`
      }`;

  // Format last message preview (truncate if too long)
  const getMessagePreview = () => {
    if (!chat.last_message) return 'Start a conversation...';
    const maxLength = isMobile ? 50 : 80;
    if (chat.last_message.length > maxLength) {
      return chat.last_message.substring(0, maxLength) + '...';
    }
    return chat.last_message;
  };

  return (
    <div
      onClick={onClick}
      className={`group ${containerClasses}`}
    >
      <div className="flex items-start gap-4">
        {/* Combined Image */}
        <div className="relative flex-shrink-0">
          <ListingImageWithStoreLogo
            listingImage={chat.listing_image}
            storeLogo={isCurrentUserBuyer ? displayImage : null}
            profilePicture={isCurrentUserSeller ? displayImage : null}
            storeName={isCurrentUserBuyer ? displayName : null}
            userName={isCurrentUserSeller ? displayName : null}
            size={isMobile ? 'lg' : 'xl'}
          />
          
          {/* Online Status Indicator */}
          {isOnline && displayIcon && (
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
          )}
          
          {/* New Message Badge */}
          {hasNewMessage && (
            <div className="absolute -top-1 -right-1">
              <div className="relative">
                <div className="animate-ping absolute inset-0 w-full h-full rounded-full bg-blue-400 opacity-75"></div>
                <Zap className="w-4 h-4 text-blue-500 bg-white rounded-full" />
              </div>
            </div>
          )}
        </div>

        {/* Chat Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`font-semibold truncate ${
                  isActive ? 'text-[#ff660e]' : 'text-gray-900 group-hover:text-[#ff660e]'
                } transition-colors ${isMobile ? 'text-base' : 'text-lg'}`}>
                  {displayName}
                </h3>
                
                {displayIcon && (
                  <div className="flex items-center gap-2">
                    {React.createElement(displayIcon, { 
                      className: "w-3 h-3 text-gray-400" 
                    })}
                    {!isMobile && isVerified && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Store Name (if seller has store) */}
              {storeName && !isMobile && (
                <p className="text-xs text-gray-500 mb-1">
                  {storeName}
                </p>
              )}
              
              <p className={`text-gray-600 line-clamp-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {getMessagePreview()}
              </p>
            </div>
            
            {/* Right side: Date + Unread indicator */}
            <div className="flex flex-col items-end ml-2">
              <div className="flex items-center gap-1 mb-2">
                <Clock className={`text-gray-400 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                <span className={`text-gray-500 whitespace-nowrap ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {formatTime(chat.last_message_at || chat.created_at)}
                </span>
              </div>
              
              {/* Unread Count Indicator */}
              {chat.unread_count > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-[#ff660e] rounded-full animate-pulse"></div>
                  <span className="text-xs font-semibold text-[#ff660e]">
                    {chat.unread_count} new
                  </span>
                </div>
              )}
              
              {/* Attachment Indicator */}
              {chat.has_attachment && (
                <div className="mt-1 flex items-center gap-1 text-gray-400">
                  <MessageSquare className="w-3 h-3" />
                  <span className="text-xs">File</span>
                </div>
              )}
            </div>
          </div>

          {/* Listing Info */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`text-gray-500 font-medium truncate ${
                isMobile ? 'text-sm max-w-[160px]' : 'text-base max-w-[300px]'
              }`}>
                {chat.listing_title}
              </span>
              {chat.listing_price && (
                <span className={`font-semibold text-gray-900 flex-shrink-0 ${
                  isMobile ? 'text-xs' : 'text-sm'
                }`}>
                  • {formatPrice(chat.listing_price)}
                </span>
              )}
            </div>
            
            {!isMobile && (
              <button className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function for price formatting
const formatPrice = (price) => {
  if (!price && price !== 0) return '₦0';
  const numericPrice = parseFloat(price);
  if (isNaN(numericPrice)) return '₦0';
  
  return `₦${numericPrice.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

export default ChatListItem;